import axios, { AxiosInstance, AxiosResponse, isAxiosError } from 'axios';
import { 
  WhatsAppClient, 
  WhatsAppMessage, 
  WhatsAppConversation, 
  WhatsAppInstance,
  AttendantSession,
  SendMessageRequest,
  AssignConversationRequest,
  TransferConversationRequest,
  WhatsAppApiResponse,
  PaginatedWhatsAppResponse,
  ConversationFilters,
  ClientFilters,
  WhatsAppMetrics,
  BatchMetrics
} from '../../types/whatsapp.types';
import {
  normalizeTemplateListResponse,
  type ZenviaTemplateListItem,
  type ZenviaTemplateCreatePayload,
  type ZenviaTemplatePatchPayload,
  type ZenviaSendTemplateTestResponse,
  type CreateZenviaCampaignRequest,
  type ZenviaCampaignListResponse,
  type ZenviaCampaignDetail,
  type ZenviaCampaignRecipientsPage,
  type CsvPreviewResponse,
  type CsvCampaignMetadata,
  type UploadCsvCampaignResponse,
  type ZenviaCampaignListFilter,
  type ZenviaCampaignRecipientsFilter,
  type ZenviaCampaignMetrics,
  type ZenviaRecreateCampaignRequest,
} from '../../types/zenvia.types';
import { API_CONFIG } from '../../config/api.config';

/** Extrai mensagem legível de erro Axios (inclui corpo texto da Zenvia). */
export function getWhatsAppApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const d = error.response?.data;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object') {
      const o = d as Record<string, unknown>;
      if (typeof o.error === 'string') return o.error;
      if (typeof o.message === 'string') return o.message;
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

export class WhatsAppApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Base da API; endpoints Zenvia em /api/v1/zenvia/
    this.baseURL = API_CONFIG.whatsappURL;
    
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Interceptor para adicionar token de autenticação
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para tratamento de respostas
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token expirado, redirecionar para login
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // ===== CLIENTES WHATSAPP =====

  async getClients(filters?: ClientFilters): Promise<PaginatedWhatsAppResponse<WhatsAppClient>> {
    const response = await this.api.get('/v1/whatsapp/clients', { params: filters });
    return response.data;
  }

  async getClientById(id: string): Promise<WhatsAppClient> {
    const response = await this.api.get(`/v1/whatsapp/clients/${id}`);
    return response.data;
  }

  async updateClient(id: string, data: Partial<WhatsAppClient>): Promise<WhatsAppClient> {
    const response = await this.api.put(`/v1/whatsapp/clients/${id}`, data);
    return response.data;
  }

  async importClientsFromExcel(file: File): Promise<WhatsAppApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.api.post('/v1/whatsapp/clients/import-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // ===== CONVERSAS =====

  async getConversations(filters?: ConversationFilters): Promise<PaginatedWhatsAppResponse<WhatsAppConversation>> {
    const response = await this.api.get('/v1/whatsapp/conversations', { params: filters });
    return response.data;
  }

  async getConversationById(id: string): Promise<WhatsAppConversation> {
    const response = await this.api.get(`/v1/whatsapp/conversations/${id}`);
    return response.data;
  }

  async getConversationHistory(conversationId: string, page = 1, limit = 50): Promise<PaginatedWhatsAppResponse<WhatsAppMessage>> {
    const response = await this.api.get(`/v1/whatsapp/conversations/${conversationId}/messages`, {
      params: { page, limit }
    });
    return response.data;
  }

  // ===== MENSAGENS =====

  async sendMessage(request: SendMessageRequest): Promise<WhatsAppApiResponse<WhatsAppMessage>> {
    const response = await this.api.post('/v1/whatsapp/messages/send', request);
    return response.data;
  }

  async sendTemplate(telefone: string, templateId: string, variaveis?: Record<string, string>): Promise<WhatsAppApiResponse<WhatsAppMessage>> {
    const response = await this.api.post('/v1/whatsapp/messages/send-template', {
      telefone,
      templateId,
      variaveis
    });
    return response.data;
  }

  async sendMediaMessage(telefone: string, mediaUrl: string, mediaType: string, caption?: string): Promise<WhatsAppApiResponse<WhatsAppMessage>> {
    const response = await this.api.post('/v1/whatsapp/messages/send-media', {
      telefone,
      mediaUrl,
      mediaType,
      caption
    });
    return response.data;
  }

  async markMessageAsRead(messageId: string): Promise<WhatsAppApiResponse<void>> {
    const response = await this.api.post(`/v1/whatsapp/messages/${messageId}/read`);
    return response.data;
  }

  // ===== TEMPLATES ZENVIA (/api/v1/zenvia/templates) =====

  async getZenviaTemplates(senderId?: string): Promise<ZenviaTemplateListItem[]> {
    const params = senderId ? { senderId } : undefined;
    const response = await this.api.get('/api/v1/zenvia/templates', { params });
    return normalizeTemplateListResponse(response.data);
  }

  /** @deprecated Use getZenviaTemplates — mantido para compatibilidade. */
  async getTemplates(senderId?: string): Promise<ZenviaTemplateListItem[]> {
    return this.getZenviaTemplates(senderId);
  }

  async getTemplateById(id: string): Promise<unknown> {
    const response = await this.api.get(`/api/v1/zenvia/templates/${encodeURIComponent(id)}`);
    return response.data;
  }

  async createZenviaTemplate(body: ZenviaTemplateCreatePayload): Promise<unknown> {
    const response = await this.api.post('/api/v1/zenvia/templates', body);
    return response.data;
  }

  /** Corpo alinhado a templates.partial-template (apenas locale, category, components, examples, notificationEmail). */
  async updateZenviaTemplate(id: string, body: ZenviaTemplatePatchPayload | Record<string, unknown>): Promise<unknown> {
    const response = await this.api.patch(`/api/v1/zenvia/templates/${encodeURIComponent(id)}`, body);
    return response.data;
  }

  async createTemplate(body: ZenviaTemplateCreatePayload): Promise<unknown> {
    return this.createZenviaTemplate(body);
  }

  async updateTemplate(id: string, body: ZenviaTemplatePatchPayload | Record<string, unknown>): Promise<unknown> {
    return this.updateZenviaTemplate(id, body);
  }

  async deleteZenviaTemplate(id: string): Promise<void> {
    await this.api.delete(`/api/v1/zenvia/templates/${encodeURIComponent(id)}`);
  }

  async deleteTemplate(id: string): Promise<WhatsAppApiResponse<void>> {
    await this.deleteZenviaTemplate(id);
    return { success: true, message: 'OK' };
  }

  async sendTemplateTest(
    to: string,
    templateId: string,
    fields?: Record<string, string>
  ): Promise<ZenviaSendTemplateTestResponse> {
    const response = await this.api.post<ZenviaSendTemplateTestResponse>(
      '/api/v1/zenvia/messages/send-template-test',
      {
        to,
        templateId,
        fields: fields && Object.keys(fields).length > 0 ? fields : undefined,
      }
    );
    return response.data;
  }

  // ===== CAMPANHAS ZENVIA (disparo interno B2) =====

  async listZenviaCampaigns(
    page = 1,
    pageSize = 20,
    filter?: ZenviaCampaignListFilter
  ): Promise<ZenviaCampaignListResponse> {
    const response = await this.api.get<ZenviaCampaignListResponse>('/api/v1/zenvia/campaigns', {
      params: {
        page,
        pageSize,
        status: filter?.status || undefined,
        search: filter?.search || undefined,
        createdFrom: filter?.createdFrom || undefined,
        createdTo: filter?.createdTo || undefined,
      },
    });
    const d = response.data;
    return {
      items: (d.items ?? []).map((x) => ({
        ...x,
        id: typeof x.id === 'string' ? x.id : String(x.id),
      })),
      total: d.total ?? 0,
      page: d.page ?? page,
      pageSize: d.pageSize ?? pageSize,
    };
  }

  async createZenviaCampaign(body: CreateZenviaCampaignRequest): Promise<{ id: string }> {
    const response = await this.api.post<{ id: string }>('/api/v1/zenvia/campaigns', body);
    const raw = response.data;
    const id = typeof raw.id === 'string' ? raw.id : String((raw as { id: unknown }).id);
    return { id };
  }

  async getZenviaCampaignRecipients(
    id: string,
    page = 1,
    pageSize = 50,
    filter?: ZenviaCampaignRecipientsFilter
  ): Promise<ZenviaCampaignRecipientsPage> {
    const response = await this.api.get<ZenviaCampaignRecipientsPage>(
      `/api/v1/zenvia/campaigns/${encodeURIComponent(id)}/recipients`,
      {
        params: {
          page,
          pageSize,
          status: filter?.status || undefined,
          search: filter?.search || undefined,
          errorContains: filter?.errorContains || undefined,
          hasError: typeof filter?.hasError === 'boolean' ? filter?.hasError : undefined,
        },
      }
    );
    return response.data;
  }

  async getZenviaCampaignMetrics(id: string): Promise<ZenviaCampaignMetrics> {
    const response = await this.api.get<ZenviaCampaignMetrics>(
      `/api/v1/zenvia/campaigns/${encodeURIComponent(id)}/metrics`
    );
    return response.data;
  }

  async exportZenviaCampaignsXlsx(filter?: ZenviaCampaignListFilter): Promise<Blob> {
    const response = await this.api.get<Blob>('/api/v1/zenvia/campaigns/export.xlsx', {
      responseType: 'blob',
      params: {
        status: filter?.status || undefined,
        search: filter?.search || undefined,
        createdFrom: filter?.createdFrom || undefined,
        createdTo: filter?.createdTo || undefined,
      },
    });
    return response.data;
  }

  async exportZenviaCampaignRecipientsXlsx(
    id: string,
    filter?: ZenviaCampaignRecipientsFilter
  ): Promise<Blob> {
    const response = await this.api.get<Blob>(
      `/api/v1/zenvia/campaigns/${encodeURIComponent(id)}/recipients/export.xlsx`,
      {
        responseType: 'blob',
        params: {
          status: filter?.status || undefined,
          search: filter?.search || undefined,
          errorContains: filter?.errorContains || undefined,
          hasError: typeof filter?.hasError === 'boolean' ? filter?.hasError : undefined,
        },
      }
    );
    return response.data;
  }

  async recreateZenviaCampaign(
    id: string,
    body: ZenviaRecreateCampaignRequest
  ): Promise<{ id: string; started: boolean }> {
    const response = await this.api.post<{ id: string; started: boolean }>(
      `/api/v1/zenvia/campaigns/${encodeURIComponent(id)}/recreate-from`,
      body
    );
    const d = response.data;
    return {
      id: typeof d.id === 'string' ? d.id : String((d as { id: unknown }).id),
      started: !!d.started,
    };
  }

  async getZenviaCampaign(id: string): Promise<ZenviaCampaignDetail> {
    const response = await this.api.get<ZenviaCampaignDetail>(`/api/v1/zenvia/campaigns/${id}`);
    const d = response.data;
    return {
      ...d,
      id: typeof d.id === 'string' ? d.id : String(d.id),
    };
  }

  async startZenviaCampaign(id: string): Promise<{ message?: string; campaignId?: string }> {
    const response = await this.api.post<{ message?: string; campaignId?: string }>(
      `/api/v1/zenvia/campaigns/${encodeURIComponent(id)}/start`
    );
    return response.data ?? {};
  }

  async cancelZenviaCampaign(id: string): Promise<void> {
    await this.api.post(`/api/v1/zenvia/campaigns/${id}/cancel`);
  }

  async retryFailedZenviaCampaign(id: string): Promise<{ message: string; count: number }> {
    const response = await this.api.post<{ message: string; count: number }>(
      `/api/v1/zenvia/campaigns/${encodeURIComponent(id)}/retry-failed`
    );
    return response.data;
  }

  async retryOneZenviaCampaignRecipient(campaignId: string, recipientId: string): Promise<{ message: string }> {
    const response = await this.api.post<{ message: string }>(
      `/api/v1/zenvia/campaigns/${encodeURIComponent(campaignId)}/retry/${encodeURIComponent(recipientId)}`
    );
    return response.data;
  }

  async previewZenviaCampaignCsv(file: File): Promise<CsvPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post<CsvPreviewResponse>(
      '/api/v1/zenvia/campaigns/preview-csv',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  }

  async uploadZenviaCampaignCsv(
    file: File,
    metadata: CsvCampaignMetadata
  ): Promise<UploadCsvCampaignResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', metadata.name.trim());
    formData.append('templateId', metadata.templateId.trim());
    if (metadata.description?.trim()) {
      formData.append('description', metadata.description.trim());
    }
    if (metadata.defaultFields && Object.keys(metadata.defaultFields).length > 0) {
      formData.append('defaultFieldsJson', JSON.stringify(metadata.defaultFields));
    }
    if (metadata.phoneColumn?.trim()) {
      formData.append('phoneColumn', metadata.phoneColumn.trim());
    }
    if (metadata.columnMapper && Object.keys(metadata.columnMapper).length > 0) {
      formData.append('columnMapperJson', JSON.stringify(metadata.columnMapper));
    }
    const response = await this.api.post<UploadCsvCampaignResponse>(
      '/api/v1/zenvia/campaigns/upload-csv',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    const raw = response.data;
    return {
      ...raw,
      id: typeof raw.id === 'string' ? raw.id : String((raw as { id: unknown }).id),
    };
  }

  // ===== ATENDIMENTO =====

  async assignConversation(request: AssignConversationRequest): Promise<WhatsAppApiResponse<AttendantSession>> {
    const response = await this.api.post('/v1/attendant/assign', request);
    return response.data;
  }

  async transferConversation(request: TransferConversationRequest): Promise<WhatsAppApiResponse<AttendantSession>> {
    const response = await this.api.post('/v1/attendant/transfer', request);
    return response.data;
  }

  async finishConversation(conversationId: string): Promise<WhatsAppApiResponse<void>> {
    const response = await this.api.post('/v1/attendant/finish', { conversationId });
    return response.data;
  }

  async getActiveConversations(colaboradorId?: string): Promise<WhatsAppConversation[]> {
    const response = await this.api.get('/v1/attendant/active', { 
      params: colaboradorId ? { colaboradorId } : {} 
    });
    return response.data;
  }

  async getAttendantMetrics(colaboradorId?: string): Promise<any> {
    const response = await this.api.get('/v1/attendant/metrics', { 
      params: colaboradorId ? { colaboradorId } : {} 
    });
    return response.data;
  }

  // ===== INSTÂNCIAS =====

  async getInstances(): Promise<WhatsAppInstance[]> {
    const response = await this.api.get('/v1/instances');
    return response.data;
  }

  async getInstanceById(id: string): Promise<WhatsAppInstance> {
    const response = await this.api.get(`/v1/instances/${id}`);
    return response.data;
  }

  async getInstanceStatus(instanceId: string): Promise<{ status: string; qrCode?: string }> {
    const response = await this.api.get(`/v1/instances/${instanceId}/status`);
    return response.data;
  }

  async reconnectInstance(instanceId: string): Promise<WhatsAppApiResponse<{ qrCode: string }>> {
    const response = await this.api.post(`/v1/instances/${instanceId}/reconnect`);
    return response.data;
  }

  async disconnectInstance(instanceId: string): Promise<WhatsAppApiResponse<void>> {
    const response = await this.api.post(`/v1/instances/${instanceId}/disconnect`);
    return response.data;
  }

  // ===== MÉTRICAS =====

  async getWhatsAppMetrics(periodo?: string): Promise<WhatsAppMetrics> {
    const response = await this.api.get('/v1/metrics/whatsapp', { 
      params: periodo ? { periodo } : {} 
    });
    return response.data;
  }

  async getBatchMetrics(periodo?: string): Promise<BatchMetrics> {
    const response = await this.api.get('/v1/metrics/batches', { 
      params: periodo ? { periodo } : {} 
    });
    return response.data;
  }

  // ===== WEBHOOKS =====

  async getWebhooks(filters?: any): Promise<any[]> {
    const response = await this.api.get('/v1/webhooks', { params: filters });
    return response.data;
  }

  async reprocessWebhook(webhookId: string): Promise<WhatsAppApiResponse<void>> {
    const response = await this.api.post(`/v1/webhooks/${webhookId}/reprocess`);
    return response.data;
  }

  // ===== UTILITÁRIOS =====

  async validatePhone(phone: string): Promise<{ valid: boolean; formatted?: string; country?: string }> {
    const response = await this.api.post('/v1/whatsapp/validate-phone', { phone });
    return response.data;
  }

  async getProfilePicture(phone: string): Promise<{ url?: string }> {
    const response = await this.api.get(`/v1/whatsapp/profile-picture/${phone}`);
    return response.data;
  }

  // ===== CONFIGURAÇÕES =====

  setBaseURL(url: string) {
    this.baseURL = url;
    this.api.defaults.baseURL = url;
  }

  getBaseURL(): string {
    return this.baseURL;
  }
}

// Instância singleton
export const whatsAppApiService = new WhatsAppApiService();
